import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useMemo } from "react"

/**
 * Hook for reading and writing query params used by Paz Centers map.
 * Supported params:
 *  - station: selected station id
 *  - region: selected region name
 *
 * Uses router.replace to avoid adding history entries while keeping URL shareable.
 */
export const usePazCentersQueryParams = () => {
    const searchParams = useSearchParams()
    const router = useRouter()
    const pathname = usePathname()

    const stationParam = searchParams.get("station") || null
    const regionParam = searchParams.get("region") || null

    const buildQueryString = useCallback((updates: Record<string, string | null | undefined>) => {
        const params = new URLSearchParams(searchParams.toString())
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                params.delete(key)
            } else {
                params.set(key, value)
            }
        })
        return params.toString()
    }, [searchParams])

    const setStationParam = useCallback((stationId: string | null) => {
        const qs = buildQueryString({ station: stationId })
        router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false })
    }, [buildQueryString, router, pathname])

    const setRegionParam = useCallback((region: string | null) => {
        const qs = buildQueryString({ region })
        router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false })
    }, [buildQueryString, router, pathname])

    const clearAllParams = useCallback(() => {
        const qs = buildQueryString({ station: null, region: null })
        router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false })
    }, [buildQueryString, router, pathname])

    return useMemo(() => ({
        stationParam,
        regionParam,
        setStationParam,
        setRegionParam,
        clearAllParams
    }), [stationParam, regionParam, setStationParam, setRegionParam, clearAllParams])
}

export type UsePazCentersQueryParamsReturn = ReturnType<typeof usePazCentersQueryParams>
